import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, Users, CreditCard, Send, CheckCircle, Shield, 
  Smartphone, Calculator, Clock, ArrowRight, ChevronRight,
  Utensils, Plane, Home, Car, Tv, PartyPopper,
  MessageCircle, IndianRupee, Sparkles, Star, Heart
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const howItWorksRef = useRef(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetStarted = () => {
    navigate('/auth/login');
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-500 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-heading font-bold">
              Split<span className="text-violet-600">Pay</span>
            </span>
          </div>
          <button
            onClick={handleGetStarted}
            className="bg-black text-white font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            data-testid="nav-cta"
          >
            SplitPay Now
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-violet-400/20 to-fuchsia-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-2xl rotate-12 opacity-60" />
        <div className="absolute bottom-40 left-20 w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-400 rounded-full opacity-60" />
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          {/* Free badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-4 py-2 rounded-full mb-6 shadow-lg animate-bounce">
            <Sparkles className="w-4 h-4" />
            100% FREE • No App Download Required
            <Sparkles className="w-4 h-4" />
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
            Split bills.{' '}
            <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
              Get paid instantly.
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            SplitPay makes it effortless to split expenses with friends and get paid directly via UPI. 
            <span className="font-bold text-black"> No calculations. No awkward reminders. No confusion.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleGetStarted}
              className="group bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-bold text-lg px-8 py-4 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center gap-2"
              data-testid="hero-cta-primary"
            >
              <Zap className="w-5 h-5" />
              SplitPay Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="bg-white text-black font-bold text-lg px-8 py-4 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              data-testid="hero-cta-secondary"
            >
              See how it works
            </button>
          </div>
          
          <div className="flex items-center justify-center gap-2 text-neutral-500">
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-400 rounded-lg flex items-center justify-center">
                <IndianRupee className="w-4 h-4 text-white" />
              </div>
            </div>
            <span className="font-medium">Built for India. Powered by UPI.</span>
          </div>
        </div>
        
        {/* Hero visual - Phone mockup */}
        <div className="max-w-md mx-auto mt-16 relative">
          <div className="bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 rounded-[3rem] p-2 shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500">
            <div className="bg-white rounded-[2.5rem] p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-heading font-bold text-lg">Quick Split</span>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">FREE</span>
              </div>
              <div className="text-center py-4">
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Total Amount</p>
                <p className="text-5xl font-heading font-bold">₹800</p>
              </div>
              <div className="bg-neutral-100 rounded-2xl p-4">
                <p className="text-xs text-neutral-500 mb-2">Split with</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white font-bold">R</div>
                  <div>
                    <p className="font-bold">Rahul</p>
                    <p className="text-sm text-neutral-500">Owes ₹400</p>
                  </div>
                </div>
              </div>
              <button className="w-full bg-black text-white font-bold py-4 rounded-full">
                Send Request via WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-8 text-center">
            Splitting money{' '}
            <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
              shouldn't be awkward.
            </span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mb-4">
                <Calculator className="w-7 h-7 text-white" />
              </div>
              <p className="text-lg font-medium">Calculating shares manually is tedious</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center mb-4">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <p className="text-lg font-medium">Reminder messages get buried</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <p className="text-lg font-medium">Payments get delayed forever</p>
            </div>
          </div>
          
          <p className="text-xl text-center text-neutral-300 leading-relaxed">
            Every group has that one person who pays for everything. Dinner, trips, cabs, subscriptions. 
            Then comes the <span className="text-white font-bold">awkward part</span> - calculating shares, 
            sending reminders, and waiting to get paid.
          </p>
          
          <div className="text-center mt-10">
            <p className="text-2xl font-heading font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              SplitPay removes the friction. Split instantly. Get paid instantly.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-6 relative">
        <div className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-cyan-400/30 to-blue-400/30 rounded-full blur-2xl" />
        
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-4 py-2 rounded-full mb-6">
                The Solution
              </div>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
                SplitPay handles everything{' '}
                <span className="bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
                  in seconds.
                </span>
              </h2>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                Enter the amount. Select your friend. SplitPay calculates the share automatically 
                and generates a UPI payment link. Send it on WhatsApp. They tap and pay.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                  <span className="font-medium">No math required</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                  <span className="font-medium">No chasing payments</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                  <span className="font-medium">Everything tracked and settled</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-violet-100 to-fuchsia-100 rounded-3xl p-8 border-2 border-black shadow-hard">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <IndianRupee className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-2xl">₹1,200</p>
                      <p className="text-neutral-500">Dinner at Bombay Canteen</p>
                    </div>
                  </div>
                  
                  <div className="h-px bg-gradient-to-r from-violet-300 to-fuchsia-300" />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 border-2 border-black">
                      <p className="text-sm text-neutral-500">You paid</p>
                      <p className="font-bold text-xl">₹1,200</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border-2 border-black">
                      <p className="text-sm text-neutral-500">They owe</p>
                      <p className="font-bold text-xl text-violet-600">₹600</p>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-500 text-white rounded-xl p-4 text-center font-bold">
                    <Zap className="w-5 h-5 inline mr-2" />
                    Payment link sent!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={howItWorksRef} className="py-20 px-6 bg-gradient-to-br from-violet-50 to-fuchsia-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
              How{' '}
              <span className="bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
                SplitPay
              </span>{' '}
              works
            </h2>
            <p className="text-lg text-neutral-600">Split in seconds. Get paid faster.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: 1, icon: IndianRupee, title: 'Enter the amount', desc: 'Paid ₹800 for dinner? Enter it once.', color: 'from-violet-500 to-purple-600' },
              { step: 2, icon: Calculator, title: 'Split instantly', desc: 'SplitPay calculates exactly who owes what.', color: 'from-fuchsia-500 to-pink-500' },
              { step: 3, icon: Send, title: 'Send payment request', desc: 'Share the UPI link via WhatsApp.', color: 'from-orange-500 to-amber-500' },
              { step: 4, icon: CheckCircle, title: 'Get paid', desc: "They tap, pay via UPI, and you're settled.", color: 'from-emerald-500 to-teal-500' },
            ].map((item) => (
              <div key={item.step} className="group">
                <div className="bg-white rounded-2xl p-6 border-2 border-black shadow-hard hover:-translate-y-2 transition-transform duration-300 h-full">
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-neutral-400">STEP {item.step}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-2">{item.title}</h3>
                  <p className="text-neutral-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Connection line */}
          <div className="hidden lg:block relative h-2 mt-8">
            <div className="absolute top-1/2 left-[12.5%] right-[12.5%] h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 via-orange-500 to-emerald-500 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
              Built for{' '}
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                real life
              </span>
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Smartphone, title: 'UPI-first experience', desc: 'Payments open directly in Google Pay, PhonePe, Paytm, or any UPI app.', color: 'from-blue-500 to-cyan-500' },
              { icon: Zap, title: 'Instant split calculation', desc: 'No manual math. SplitPay calculates everything automatically.', color: 'from-violet-500 to-purple-500' },
              { icon: Clock, title: 'Simple and fast', desc: 'Create and send a split in under 10 seconds.', color: 'from-emerald-500 to-teal-500' },
              { icon: CheckCircle, title: 'Clear payment tracking', desc: "See who paid and who hasn't.", color: 'from-pink-500 to-rose-500' },
              { icon: Shield, title: 'No bank details required', desc: 'Just your phone number and UPI ID.', color: 'from-amber-500 to-orange-500' },
              { icon: Heart, title: 'Completely FREE', desc: 'No hidden charges. No premium plans. Just free.', color: 'from-red-500 to-pink-500' },
            ].map((feature, i) => (
              <div key={i} className="group bg-white rounded-2xl p-6 border-2 border-black shadow-hard hover:-translate-y-1 transition-all">
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">{feature.title}</h3>
                <p className="text-neutral-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
              Perfect for{' '}
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                every shared expense
              </span>
            </h2>
            <p className="text-lg text-neutral-600">Anywhere money is shared, SplitPay makes settlement easy.</p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: Utensils, label: 'Dinner with friends', color: 'from-orange-500 to-red-500' },
              { icon: Plane, label: 'Trips and travel', color: 'from-cyan-500 to-blue-500' },
              { icon: Home, label: 'Roommate expenses', color: 'from-emerald-500 to-teal-500' },
              { icon: Car, label: 'Cab rides', color: 'from-yellow-500 to-amber-500' },
              { icon: Tv, label: 'Subscriptions', color: 'from-violet-500 to-purple-500' },
              { icon: PartyPopper, label: 'Events & parties', color: 'from-pink-500 to-rose-500' },
            ].map((item, i) => (
              <div key={i} className="group bg-white rounded-2xl p-6 border-2 border-black shadow-hard hover:scale-105 transition-transform text-center">
                <div className={`w-16 h-16 mx-auto bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-3 group-hover:rotate-6 transition-transform`}>
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <p className="font-bold text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why SplitPay Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: MessageCircle, title: 'No awkward reminders', desc: 'Payment requests are clear and direct.', color: 'bg-gradient-to-br from-violet-500 to-purple-600' },
                  { icon: CheckCircle, title: 'No confusion', desc: 'Everyone sees exactly what they owe.', color: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
                  { icon: Zap, title: 'No middleman', desc: 'Payments go directly via UPI.', color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
                  { icon: Star, title: 'Made for India', desc: 'Built around how people actually use UPI.', color: 'bg-gradient-to-br from-pink-500 to-rose-500' },
                ].map((item, i) => (
                  <div key={i} className={`${item.color} rounded-2xl p-6 text-white border-2 border-black shadow-hard`}>
                    <item.icon className="w-8 h-8 mb-3" />
                    <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-sm text-white/80">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
                Why people choose{' '}
                <span className="bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
                  SplitPay
                </span>
              </h2>
              <p className="text-lg text-neutral-600 mb-6">
                We built SplitPay because we were tired of the same problems. Awkward messages, 
                forgotten payments, and endless calculations.
              </p>
              <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded-2xl p-6 border-2 border-emerald-300">
                <p className="font-bold text-emerald-800 text-xl mb-2">
                  "SplitPay kar de" - Just split it!
                </p>
                <p className="text-emerald-700">
                  The simplest way to handle shared expenses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full mb-6">
            <Shield className="w-10 h-10" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
            Secure and{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              private
            </span>
          </h2>
          <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
            SplitPay never stores your bank details. Payments happen directly via UPI.
            Your data is private. Your payments stay between you and your friends.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <span className="font-medium">No bank details stored</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <span className="font-medium">Direct UPI payments</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <span className="font-medium">Private & secure</span>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-8 text-center text-white">
            <div>
              <p className="text-5xl font-heading font-bold mb-2">10K+</p>
              <p className="text-white/80 font-medium">Splits created</p>
            </div>
            <div>
              <p className="text-5xl font-heading font-bold mb-2">₹50L+</p>
              <p className="text-white/80 font-medium">Payments settled</p>
            </div>
            <div>
              <p className="text-5xl font-heading font-bold mb-2">4.9★</p>
              <p className="text-white/80 font-medium">User rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-100 via-fuchsia-100 to-pink-100" />
        <div className="absolute top-10 left-10 w-40 h-40 bg-gradient-to-br from-violet-400/30 to-fuchsia-400/30 rounded-full blur-2xl" />
        <div className="absolute bottom-10 right-10 w-60 h-60 bg-gradient-to-br from-orange-400/30 to-amber-400/30 rounded-full blur-2xl" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6">
            Stop chasing{' '}
            <span className="bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
              payments.
            </span>
          </h2>
          <p className="text-xl text-neutral-600 mb-8">
            Split bills and settle instantly with SplitPay.
          </p>
          
          {/* Free emphasis */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-6 py-3 rounded-full mb-8 shadow-lg">
            <Sparkles className="w-5 h-5" />
            Completely FREE • No App Download • Works on any browser
            <Sparkles className="w-5 h-5" />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="group bg-black text-white font-bold text-xl px-10 py-5 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center gap-3"
              data-testid="final-cta-primary"
            >
              <Zap className="w-6 h-6" />
              SplitPay Now
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="bg-white text-black font-bold text-xl px-10 py-5 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              data-testid="final-cta-secondary"
            >
              Learn more
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-heading font-bold">
                  Split<span className="text-violet-400">Pay</span>
                </span>
              </div>
              <p className="text-neutral-400 mb-4">
                Split bills. Get paid instantly.
              </p>
              <p className="text-sm text-neutral-500">
                Never chase payments again. The simplest way to handle shared expenses.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-neutral-400">
                <li><button onClick={scrollToHowItWorks} className="hover:text-white transition-colors">How it works</button></li>
                <li><button onClick={handleGetStarted} className="hover:text-white transition-colors">Get started</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-neutral-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-neutral-800 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-neutral-500 text-sm">
              © 2024 SplitPay. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-neutral-500 text-sm">
              <span>Built with</span>
              <Heart className="w-4 h-4 text-red-500 fill-red-500" />
              <span>in India</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
